// ─── FuelOpsDashboard ───────────────────────────────────────────────
// Dashboard dedicado a operações de distribuição de combustível.
// Só renderizado para Move Express (gated no AppLayout). Mostra KPIs
// operacionais + gráficos por período seleccionável.
import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Button, TextField, IconButton,
  // Alias: `Tooltip` sem prefixo é o do recharts, usado nos gráficos abaixo.
  Tooltip as MuiTooltip,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles } from '@material-ui/core/styles';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from 'recharts';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import SpeedIcon from '@material-ui/icons/Speed';
import RoomIcon from '@material-ui/icons/Room';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import TimelineIcon from '@material-ui/icons/Timeline';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import { AuthContext } from '../../App';

const useStyles = makeStyles(() => ({
  root: {
    flex: 1, overflow: 'auto', padding: 24, background: '#f8fafc',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 20, flexWrap: 'wrap', gap: 12,
  },
  title: {
    fontSize: '1.5rem', fontWeight: 800, color: '#0f172a',
    letterSpacing: '-0.02em',
    display: 'flex', alignItems: 'center', gap: 10,
  },
  titleBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: '#fff7ed', color: '#9a3412',
    border: '1px solid #fed7aa', borderRadius: 999,
    padding: '4px 10px', fontSize: '0.66rem', fontWeight: 800,
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  periodBar: {
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    background: '#fff', padding: '8px 12px', borderRadius: 10,
    border: '1px solid #e2e8f0',
  },
  periodChip: {
    fontSize: '0.74rem', fontWeight: 700, color: '#475569',
    padding: '5px 12px', borderRadius: 8, cursor: 'pointer',
    border: '1px solid #e2e8f0', background: '#fff',
    textTransform: 'none',
  },
  periodChipActive: {
    background: 'linear-gradient(135deg, #fb923c, #f97316)',
    color: '#fff', border: '1px solid #ea580c',
    boxShadow: '0 2px 6px rgba(249,115,22,0.25)',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 14, marginBottom: 24,
    '@media (max-width: 1100px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
  },
  kpiCard: {
    background: '#fff', borderRadius: 14, padding: '16px 18px',
    boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
    display: 'flex', alignItems: 'flex-start', gap: 12,
    borderLeft: '4px solid transparent',
    transition: 'all 0.18s ease',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(15,23,42,0.10)' },
  },
  kpiIcon: {
    width: 44, height: 44, borderRadius: 11,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  kpiLabel: {
    fontSize: '0.6rem', fontWeight: 800, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4,
  },
  kpiValue: {
    fontSize: '1.55rem', fontWeight: 800, color: '#0f172a',
    fontFamily: 'ui-monospace, Menlo, monospace', lineHeight: 1.05,
  },
  kpiSub: {
    fontSize: '0.66rem', color: '#94a3b8', marginTop: 4,
  },
  row2: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24,
    '@media (max-width: 1100px)': { gridTemplateColumns: '1fr' },
  },
  card: {
    background: '#fff', borderRadius: 14, padding: 20,
    boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
  },
  cardTitle: {
    fontSize: '0.78rem', fontWeight: 800, color: '#0f172a',
    marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  // Nota metodológica — ícone discreto + tooltip rico (em vez de um parágrafo
  // a poluir o cartão).
  infoIcon: {
    fontSize: 15, color: '#cbd5e1', cursor: 'help', flexShrink: 0,
    transition: 'color 0.15s ease',
    '&:hover': { color: '#16a34a' },
  },
  infoTip: {
    background: '#ffffff',
    color: '#334155',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 12px 32px rgba(15,23,42,0.16)',
    padding: '14px 16px',
    maxWidth: 380,
  },
  tipTitle: {
    fontSize: '0.7rem', fontWeight: 800, color: '#0f172a',
    textTransform: 'uppercase', letterSpacing: 0.5,
    paddingBottom: 8, marginBottom: 10, borderBottom: '1px solid #f1f5f9',
  },
  tipRow: { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 10 },
  tipTerm: { fontSize: '0.76rem', fontWeight: 800, color: '#0f172a' },
  tipStar: { fontSize: '0.68rem' },
  tipTxt: { fontSize: '0.74rem', color: '#64748b', lineHeight: 1.5 },
  tipFoot: {
    fontSize: '0.68rem', color: '#94a3b8', fontStyle: 'italic',
    paddingTop: 8, marginTop: 2, borderTop: '1px solid #f1f5f9',
  },
  vehicleTable: {
    width: '100%', borderCollapse: 'collapse',
    '& th': {
      textAlign: 'left', fontSize: '0.62rem', fontWeight: 800, color: '#64748b',
      textTransform: 'uppercase', letterSpacing: 0.5,
      padding: '8px 10px', borderBottom: '2px solid #e2e8f0',
    },
    '& td': {
      fontSize: '0.78rem', color: '#0f172a',
      padding: '10px 10px', borderBottom: '1px solid #f1f5f9',
    },
    '& tbody tr:hover': { background: '#fafaf9' },
  },
  utilBar: {
    width: '100%', height: 8, background: '#f1f5f9',
    borderRadius: 999, overflow: 'hidden', position: 'relative',
  },
  utilFill: {
    height: '100%', borderRadius: 999,
    transition: 'width 0.3s ease',
  },
  emptyState: {
    textAlign: 'center', padding: 40, color: '#94a3b8',
    fontSize: '0.86rem', fontStyle: 'italic',
  },
}));

const CUSTOMER_LABELS = {
  sonangol: 'Sonangol',
  total: 'Total',
  pumangol: 'Pumangol',
  sonangalp: 'Sonangalp',
  outro: 'Outro',
};

const CUSTOMER_COLORS = {
  sonangol: '#dc2626',
  total: '#1e40af',
  pumangol: '#7c3aed',
  sonangalp: '#0891b2',
  outro: '#64748b',
};

function fmtNumber(n, lang = 'pt') {
  if (n == null || !Number.isFinite(n)) return '—';
  return Number(n).toLocaleString(lang === 'pt' ? 'pt-PT' : 'en-GB');
}
function fmtDate(d) {
  return new Date(d).toISOString().slice(0, 10);
}
function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

// Horas decimais → "6h 12m". O formato decimal (6.2h) era ambíguo: podia ler-se
// como 6h02 ou 6h20, quando na verdade são 6h12 (0,2 × 60 min).
function fmtHm(hoursDec, lang = 'pt') {
  const total = Math.max(0, Math.round((Number(hoursDec) || 0) * 60)); // minutos
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function FuelOpsDashboard({ lang = 'pt' }) {
  const classes = useStyles();
  const { token, modules, clientName } = useContext(AuthContext);
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  // Gating por módulo (fail-open sem lista) — KPIs Acidentes/Alarmes.
  const hasModule = (m) => !modules || modules.length === 0 || modules.includes(m);

  // Período (default: últimos 30 dias)
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(new Date());
  const [data, setData] = useState(null);
  const [divTotals, setDivTotals] = useState(null);
  const [speedAlarms, setSpeedAlarms] = useState(null); // { count, available } do EcooGPS
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Pesquisa por VIATURA (matrícula) — quando preenchida, todo o resumo passa
  // a ser só dessa viatura (pedido Move Express). Vazio = frota inteira.
  const [vehicleQuery, setVehicleQuery] = useState('');
  const [vehicleApplied, setVehicleApplied] = useState(''); // a que está de facto aplicada

  const fetchStats = async (f, t, veh = vehicleApplied) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    const vq = (veh || '').trim() ? `&vehicle=${encodeURIComponent((veh || '').trim())}` : '';
    try {
      const url = `/planner-api/fuel-stats/operations?from=${encodeURIComponent(f.toISOString())}&to=${encodeURIComponent(t.toISOString())}${vq}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || 'Erro');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // Divergências (carga vs entrega) — mesma fonte do Relatório de Divergências.
    // Não bloqueia o dashboard principal se falhar.
    try {
      const dUrl = `/planner-api/stats/divergences?dateFrom=${fmtDate(f)}&dateTo=${fmtDate(t)}${vq}`;
      const dr = await fetch(dUrl, { headers: { Authorization: `Bearer ${token}` } });
      const dj = await dr.json();
      setDivTotals(dr.ok ? (dj.totals || null) : null);
    } catch {
      setDivTotals(null);
    }
    // Alarmes de velocidade (EcooGPS) — endpoint separado, best-effort.
    try {
      const sUrl = `/planner-api/fuel-stats/speed-alarms?from=${encodeURIComponent(f.toISOString())}&to=${encodeURIComponent(t.toISOString())}${vq}`;
      const sr = await fetch(sUrl, { headers: { Authorization: `Bearer ${token}` } });
      const sj = await sr.json();
      setSpeedAlarms(sr.ok ? sj : null);
    } catch {
      setSpeedAlarms(null);
    }
  };

  // Aplica/limpa a pesquisa de viatura e refresca com o período atual.
  function applyVehicle(veh) {
    const v = (veh || '').trim();
    setVehicleApplied(v);
    setVehicleQuery(v);
    fetchStats(from, to, v);
  }

  useEffect(() => {
    fetchStats(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function setPeriod(days) {
    const newFrom = daysAgo(days);
    const newTo = new Date();
    setFrom(newFrom);
    setTo(newTo);
    fetchStats(newFrom, newTo);
  }

  function applyCustomDates() {
    fetchStats(from, to);
  }

  const k = data?.kpis || {};
  const ws = data?.workSchedule || null; // base do indicador "utilização útil"
  const hasData = data && (k.routesCount > 0 || k.otsCount > 0);

  // Dados para barchart de litros entregues por cliente final
  const customerChartData = useMemo(() => {
    if (!data?.deliveredByCustomer) return [];
    return data.deliveredByCustomer.map(c => ({
      name: CUSTOMER_LABELS[c.customer] || c.customer || '—',
      litres: c.litres,
      color: CUSTOMER_COLORS[c.customer] || '#64748b',
    }));
  }, [data]);

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.title}>
            <Box style={{
              width: 44, height: 44, borderRadius: 11,
              background: 'linear-gradient(135deg, #fb923c, #f97316)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 3px 10px rgba(249,115,22,0.30)',
            }}>
              <LocalGasStationIcon style={{ fontSize: 24 }} />
            </Box>
            {T('Operações Combustível', 'Fuel Operations')}
            {clientName && <span className={classes.titleBadge}>{clientName}</span>}
          </Typography>
          <Typography style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 2 }}>
            {T('Indicadores essenciais de gestão de transporte', 'Essential transport management indicators')}
          </Typography>
        </Box>
        <Box className={classes.periodBar}>
          {[
            { label: T('Hoje', 'Today'), days: 1 },
            { label: T('Semana', 'Week'), days: 7 },
            { label: T('Mês', 'Month'), days: 30 },
            { label: T('3 meses', '3 months'), days: 90 },
          ].map(opt => {
            const isActive = Math.round((to - from) / 86400000) === opt.days;
            return (
              <Button
                key={opt.days}
                onClick={() => setPeriod(opt.days)}
                className={`${classes.periodChip} ${isActive ? classes.periodChipActive : ''}`}
                style={isActive ? {
                  background: 'linear-gradient(135deg, #fb923c, #f97316)',
                  color: '#fff', border: '1px solid #ea580c',
                  boxShadow: '0 2px 6px rgba(249,115,22,0.25)',
                } : undefined}
              >
                {opt.label}
              </Button>
            );
          })}
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 8, borderLeft: '1px solid #e2e8f0' }}>
            <TextField
              size="small" variant="outlined" type="date"
              value={fmtDate(from)}
              onChange={e => setFrom(new Date(e.target.value))}
              InputProps={{ style: { fontSize: '0.74rem' } }}
              InputLabelProps={{ shrink: true }}
            />
            <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>→</span>
            <TextField
              size="small" variant="outlined" type="date"
              value={fmtDate(to)}
              onChange={e => setTo(new Date(e.target.value))}
              InputProps={{ style: { fontSize: '0.74rem' } }}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              size="small" variant="contained"
              onClick={applyCustomDates}
              style={{ textTransform: 'none', fontSize: '0.72rem', background: '#0f172a', color: '#fff', fontWeight: 700 }}
            >
              {T('Aplicar', 'Apply')}
            </Button>
          </Box>
          <IconButton size="small" onClick={() => fetchStats(from, to)} title={T('Actualizar', 'Refresh')}>
            <RefreshIcon style={{ fontSize: 18, color: '#64748b' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Pesquisa por VIATURA — o resumo passa a ser só dessa viatura no
          período escolhido (pedido Move Express). Vazio = frota inteira. */}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <TextField
          size="small" variant="outlined"
          placeholder={T('Pesquisar viatura por matrícula…', 'Search vehicle by plate…')}
          value={vehicleQuery}
          onChange={e => setVehicleQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') applyVehicle(vehicleQuery); }}
          InputProps={{
            style: { fontSize: '0.8rem' },
            startAdornment: <SearchIcon style={{ fontSize: 18, color: '#94a3b8', marginRight: 6 }} />,
          }}
          style={{ minWidth: 280 }}
        />
        <Button
          size="small" variant="contained"
          onClick={() => applyVehicle(vehicleQuery)}
          style={{ textTransform: 'none', fontSize: '0.76rem', fontWeight: 700, background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff' }}
        >
          {T('Pesquisar', 'Search')}
        </Button>
        {vehicleApplied ? (
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 12px', borderRadius: 999, background: '#fff7ed', border: '1px solid #fed7aa' }}>
            <Typography style={{ fontSize: '0.76rem', fontWeight: 700, color: '#9a3412' }}>
              {T('A mostrar', 'Showing')}: {vehicleApplied}
            </Typography>
            <IconButton size="small" onClick={() => applyVehicle('')} title={T('Ver toda a frota', 'Show whole fleet')} style={{ padding: 3 }}>
              <CloseIcon style={{ fontSize: 15, color: '#9a3412' }} />
            </IconButton>
          </Box>
        ) : (
          <Typography style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
            {T('A mostrar toda a frota', 'Showing whole fleet')}
          </Typography>
        )}
      </Box>

      {loading && (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress style={{ color: '#f97316' }} />
        </Box>
      )}
      {error && (
        <Box style={{
          background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b',
          padding: '10px 14px', borderRadius: 8, fontSize: '0.78rem', marginBottom: 16,
        }}>⚠ {error}</Box>
      )}

      {!loading && !error && !hasData && (
        <Box className={classes.card}>
          <Typography className={classes.emptyState}>
            {T('Sem dados de OTs ou rotas no período seleccionado.', 'No OT or route data for the selected period.')}
          </Typography>
        </Box>
      )}

      {!loading && hasData && (
        <>
          {/* KPIs */}
          <Box className={classes.kpiGrid}>
            <KpiCard
              icon={<SpeedIcon style={{ fontSize: 22 }} />}
              iconBg="linear-gradient(135deg, #16a34a, #15803d)"
              borderColor="#16a34a"
              label={T('Taxa utilização frota', 'Fleet utilization rate')}
              value={`${k.avgUtilizationPct.toFixed(1)}%`}
              sub={ws
                ? T(`do horário de trabalho (${ws.hoursPerDay}h/dia, ${ws.workDaysLabel}) · ${k.routesCount} rotas`,
                    `of work schedule (${ws.hoursPerDay}h/day, ${ws.workDaysLabel}) · ${k.routesCount} routes`)
                : T(`${k.routesCount} rotas no período`, `${k.routesCount} routes in period`)}
              classes={classes}
            />
            <KpiCard
              icon={<RoomIcon style={{ fontSize: 22 }} />}
              iconBg="linear-gradient(135deg, #8b5cf6, #7c3aed)"
              borderColor="#8b5cf6"
              label={T('Média paragens/rota', 'Avg stops/route')}
              value={k.avgStopsPerRoute.toFixed(1)}
              sub={T('estimativa: 2/OT + base', 'estimate: 2/OT + base')}
              classes={classes}
            />
            <KpiCard
              icon={<LocalGasStationIcon style={{ fontSize: 22 }} />}
              iconBg="linear-gradient(135deg, #fb923c, #f97316)"
              borderColor="#f97316"
              label={T('Litros carregados (TCPL/IBV)', 'Loaded litres (TCPL/IBV)')}
              value={fmtNumber(k.totalLoadedLitres, lang)}
              sub={`${k.totalLoadings} ${T('carregamentos', 'loadings')}`}
              classes={classes}
            />
            <KpiCard
              icon={<LocalShippingIcon style={{ fontSize: 22 }} />}
              iconBg="linear-gradient(135deg, #1e40af, #1e3a8a)"
              borderColor="#1e40af"
              label={T('Litros entregues (PAs)', 'Delivered litres (PAs)')}
              value={fmtNumber(k.totalDeliveredLitres, lang)}
              sub={`${k.totalDeliveries} ${T('entregas', 'deliveries')}`}
              classes={classes}
            />
            {/* Km percorridos totais — soma das distâncias das rotas fuel */}
            <KpiCard
              icon={<TimelineIcon style={{ fontSize: 22 }} />}
              iconBg="linear-gradient(135deg, #0ea5e9, #0284c7)"
              borderColor="#0ea5e9"
              label={T('Km percorridos totais', 'Total km driven')}
              value={`${fmtNumber(k.totalKm || 0, lang)} km`}
              sub={T(`${k.routesCount || 0} rotas no período`, `${k.routesCount || 0} routes in period`)}
              classes={classes}
            />
            {/* Acidentes — módulo 'accidents' */}
            {hasModule('accidents') && (
            <KpiCard
              icon={<ReportProblemIcon style={{ fontSize: 22 }} />}
              iconBg="linear-gradient(135deg, #f43f5e, #e11d48)"
              borderColor="#f43f5e"
              label={T('Acidentes', 'Accidents')}
              value={fmtNumber(k.accidentsCount || 0, lang)}
              sub={T('no período', 'in period')}
              classes={classes}
            />
            )}
            {/* Alarmes de velocidade — módulo 'speed_alarms' (EcooGPS, best-effort) */}
            {hasModule('speed_alarms') && (
            <KpiCard
              icon={<SpeedIcon style={{ fontSize: 22 }} />}
              iconBg="linear-gradient(135deg, #f59e0b, #d97706)"
              borderColor="#f59e0b"
              label={T('Alarmes de velocidade', 'Speed alarms')}
              value={speedAlarms && speedAlarms.available && speedAlarms.count != null
                ? fmtNumber(speedAlarms.count, lang) : '—'}
              sub={speedAlarms && speedAlarms.available
                ? T('excesso de velocidade (EcooGPS)', 'overspeed (EcooGPS)')
                : T('EcooGPS indisponível', 'EcooGPS unavailable')}
              classes={classes}
            />
            )}
          </Box>

          {/* Divergências carga vs entrega — do Relatório de Divergências.
              IMPORTANTE: estes cartões comparam carga vs entrega DA MESMA OT (só
              OTs já entregues), por isso são AUTO-CONSISTENTES:
              Carregado(entregues) − Entregue = Divergência. Não confundir com os
              cartões de topo "Litros carregados/entregues", que são totais de
              TODAS as cargas (73) e entregas (60) — conjuntos diferentes (há OTs
              carregadas ainda por entregar), logo a subtracção desses NÃO dá a
              divergência. */}
          {divTotals && (
            <Box className={classes.kpiGrid} style={{ marginTop: 4 }}>
              <KpiCard
                icon={<LocalGasStationIcon style={{ fontSize: 22 }} />}
                iconBg="linear-gradient(135deg, #fb923c, #f97316)"
                borderColor="#f97316"
                label={T('Carregado (OTs entregues)', 'Loaded (delivered OTs)')}
                value={`${fmtNumber(divTotals.loaded || 0, lang)} L`}
                sub={T('litros carregados nessas OTs', 'loaded litres in those OTs')}
                classes={classes}
              />
              {/* Cartão "Entregue (OTs entregues)" removido a pedido do cliente:
                  o valor era sempre igual ao de topo "Litros entregues (PAs)"
                  (uma entrega só existe se a OT foi entregue → conta nos dois).
                  A divergência continua coerente: Carregado(OTs entregues) −
                  Divergência = Entregue. */}
              <KpiCard
                icon={<CompareArrowsIcon style={{ fontSize: 22 }} />}
                iconBg="linear-gradient(135deg, #fb923c, #ea580c)"
                borderColor="#ea580c"
                label={T('Divergência total', 'Total divergence')}
                value={`${fmtNumber(divTotals.divergence || 0, lang)} L`}
                sub={T('carga − entrega, por OT', 'load − delivery, per OT')}
                classes={classes}
              />
              <KpiCard
                icon={<ErrorOutlineIcon style={{ fontSize: 22 }} />}
                iconBg="linear-gradient(135deg, #f43f5e, #e11d48)"
                borderColor="#e11d48"
                label={T('Com divergência', 'With divergence')}
                value={String(divTotals.withDivergence || 0)}
                sub={T('OTs com desvio de litros', 'OTs with litre deviation')}
                classes={classes}
              />
            </Box>
          )}

          {/* Row gráficos */}
          <Box className={classes.row2}>
            {/* Litros carregados por terminal */}
            <Box className={classes.card}>
              <Typography className={classes.cardTitle}>
                <LocalGasStationIcon style={{ fontSize: 16, color: '#ea580c' }} />
                {T('Carregamentos por terminal', 'Loadings by terminal')}
              </Typography>
              {data.loadedByTerminal.length === 0 ? (
                <Typography className={classes.emptyState}>{T('Sem carregamentos no período', 'No loadings in period')}</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(200, data.loadedByTerminal.length * 42)}>
                  <BarChart data={data.loadedByTerminal} layout="vertical" margin={{ top: 5, right: 60, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <YAxis type="category" dataKey="terminal" width={140} tick={{ fontSize: 11, fill: '#334155' }} />
                    <Tooltip formatter={v => [`${fmtNumber(v, lang)} L`, T('Litros', 'Litres')]} />
                    <Bar dataKey="litres" radius={[0, 6, 6, 0]} barSize={22}>
                      {data.loadedByTerminal.map((_, i) => (
                        <Cell key={i} fill="#f97316" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>

            {/* Litros entregues por cliente final */}
            <Box className={classes.card}>
              <Typography className={classes.cardTitle}>
                <LocalShippingIcon style={{ fontSize: 16, color: '#1e40af' }} />
                {T('Entregas por cliente final', 'Deliveries by final customer')}
              </Typography>
              {customerChartData.length === 0 ? (
                <Typography className={classes.emptyState}>{T('Sem entregas no período', 'No deliveries in period')}</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(200, customerChartData.length * 42)}>
                  <BarChart data={customerChartData} layout="vertical" margin={{ top: 5, right: 60, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12, fill: '#334155', fontWeight: 600 }} />
                    <Tooltip formatter={v => [`${fmtNumber(v, lang)} L`, T('Litros', 'Litres')]} />
                    <Bar dataKey="litres" radius={[0, 6, 6, 0]} barSize={26}>
                      {customerChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Box>

          {/* Entregas detalhe por PA + Tabela utilização */}
          <Box className={classes.row2}>
            <Box className={classes.card}>
              <Typography className={classes.cardTitle}>
                <RoomIcon style={{ fontSize: 16, color: '#dc2626' }} />
                {T('Entregas por PA (top 10)', 'Deliveries by PA (top 10)')}
              </Typography>
              {data.deliveredByPost.length === 0 ? (
                <Typography className={classes.emptyState}>{T('Sem entregas no período', 'No deliveries in period')}</Typography>
              ) : (
                <table className={classes.vehicleTable}>
                  <thead>
                    <tr>
                      <th>{T('Posto', 'Post')}</th>
                      <th style={{ textAlign: 'right' }}>{T('Entregas', 'Deliveries')}</th>
                      <th style={{ textAlign: 'right' }}>{T('Litros', 'Litres')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.deliveredByPost.slice(0, 10).map((p, i) => (
                      <tr key={i}>
                        <td>{p.post}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, Menlo, monospace' }}>{p.deliveries}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700 }}>{fmtNumber(p.litres, lang)} L</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Box>

            <Box className={classes.card}>
              <Typography className={classes.cardTitle}>
                <SpeedIcon style={{ fontSize: 16, color: '#16a34a' }} />
                {T('Utilização por viatura (top 10)', 'Utilization by vehicle (top 10)')}
                {/* Nota metodológica — ícone discreto, detalhe no hover. */}
                <MuiTooltip
                  interactive
                  placement="right-start"
                  classes={{ tooltip: classes.infoTip }}
                  title={
                    <Box>
                      <Typography className={classes.tipTitle}>{T('Como se lê este quadro', 'How to read this table')}</Typography>

                      <Box className={classes.tipRow}>
                        <span className={classes.tipTerm}>{T('Tempo em serviço', 'Time on duty')}</span>
                        <span className={classes.tipTxt}>
                          {T('Desde que a viatura sai da base até regressar, somado em todas as OTs do período.',
                             'From leaving base until returning, summed over all orders in the period.')}
                        </span>
                      </Box>

                      <Box className={classes.tipRow}>
                        <span className={classes.tipTerm} style={{ color: '#16a34a' }}>
                          {T('Utilização útil', 'Business utilization')} <span className={classes.tipStar}>★</span>
                        </span>
                        <span className={classes.tipTxt}>
                          {ws
                            ? T(`Tempo em serviço ÷ horário de trabalho (${ws.hoursPerDay}h/dia, ${ws.workDaysLabel}, sem feriados ${ws.country}) = ${fmtHm(ws.businessHours, lang)} neste período. É o indicador a olhar.`,
                                `Time on duty ÷ work schedule (${ws.hoursPerDay}h/day, ${ws.workDaysLabel}, excl. ${ws.country} holidays) = ${fmtHm(ws.businessHours, lang)} in this period. This is the one to watch.`)
                            : T('Tempo em serviço ÷ horário de trabalho. É o indicador a olhar.', 'Time on duty ÷ work schedule. The one to watch.')}
                        </span>
                      </Box>

                      <Box className={classes.tipRow}>
                        <span className={classes.tipTerm}>{T('Utilização 24/7', 'Utilization 24/7')}</span>
                        <span className={classes.tipTxt}>
                          {T('Tempo em serviço ÷ período inteiro, noites e fins-de-semana incluídos. Serve só de contexto — uma cisterna nunca anda 24/7.',
                             'Time on duty ÷ the whole period, nights and weekends included. Context only — a tanker never runs 24/7.')}
                        </span>
                      </Box>

                      <Typography className={classes.tipFoot}>
                        {T('O horário de trabalho define-se no Perfil (Preferências → Geral).',
                           'The work schedule is set in the Profile (Preferences → General).')}
                      </Typography>
                    </Box>
                  }
                >
                  <InfoOutlinedIcon className={classes.infoIcon} />
                </MuiTooltip>
              </Typography>
              {data.vehicleUtilization.length === 0 ? (
                <Typography className={classes.emptyState}>{T('Sem viaturas activas no período', 'No active vehicles in period')}</Typography>
              ) : (
                <table className={classes.vehicleTable}>
                  <thead>
                    <tr>
                      <th>{T('Matrícula', 'Plate')}</th>
                      <th style={{ textAlign: 'right' }}>{T('Rotas', 'Routes')}</th>
                      <th style={{ textAlign: 'right' }} title={T('Tempo total em serviço (saída da base → regresso)', 'Total time on duty (base departure → return)')}>
                        {T('Tempo em serviço', 'Time on duty')}
                      </th>
                      <th title={ws
                        ? T(`Tempo em serviço ÷ horário de trabalho (${ws.hoursPerDay}h/dia, ${ws.workDaysLabel}, sem feriados ${ws.country})`,
                            `Time on duty ÷ work schedule (${ws.hoursPerDay}h/day, Mon–Fri, excl. ${ws.country} holidays)`)
                        : T('Tempo em serviço ÷ horário de trabalho', 'Time on duty ÷ work schedule')}>
                        {T('Utilização útil', 'Business utilization')}
                      </th>
                      <th title={T('Tempo em serviço ÷ duração total do período (24h/dia, noites e fins-de-semana incluídos)', 'Time on duty ÷ whole period (24/7)')}>
                        {T('Utilização 24/7', 'Utilization 24/7')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.vehicleUtilization.slice(0, 10).map((v, i) => {
                      const bizPct = v.utilizationBusinessPct ?? 0;
                      const bizColor = bizPct >= 60 ? '#16a34a' : bizPct >= 30 ? '#f59e0b' : '#dc2626';
                      return (
                        <tr key={i}>
                          <td style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700 }}>{v.plate}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, Menlo, monospace' }}>{v.routesCount}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, Menlo, monospace' }}
                              title={T(`${v.hoursActive.toFixed(2)} horas em ${v.routesCount} rota(s)`, `${v.hoursActive.toFixed(2)} hours over ${v.routesCount} route(s)`)}>
                            {fmtHm(v.hoursActive, lang)}
                          </td>
                          {/* Utilização ÚTIL — contra o horário de trabalho */}
                          <td>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Box className={classes.utilBar}>
                                <Box className={classes.utilFill} style={{ width: `${bizPct}%`, background: bizColor }} />
                              </Box>
                              <span style={{ fontSize: '0.75rem', fontWeight: 800, color: bizColor, fontFamily: 'ui-monospace, Menlo, monospace', minWidth: 42, textAlign: 'right' }}>
                                {bizPct.toFixed(0)}%
                              </span>
                            </Box>
                          </td>
                          {/* Utilização 24/7 — contexto, secundária */}
                          <td>
                            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                              {v.utilizationPct.toFixed(0)}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

function KpiCard({ icon, iconBg, borderColor, label, value, sub, classes }) {
  return (
    <Box className={classes.kpiCard} style={{ borderLeftColor: borderColor }}>
      <Box className={classes.kpiIcon} style={{ background: iconBg, color: '#fff' }}>
        {icon}
      </Box>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography className={classes.kpiLabel}>{label}</Typography>
        <Typography className={classes.kpiValue}>{value}</Typography>
        {sub && <Typography className={classes.kpiSub}>{sub}</Typography>}
      </Box>
    </Box>
  );
}

export default FuelOpsDashboard;
